import { useState, useMemo } from "react";
import {
  Compass,
  MapPin,
  ChevronRight,
  ChevronLeft,
} from "lucide-react";
import { ExperienceDto } from "@/types";
import { useNavigation } from "@/context/NavigationContext";
import { useAuth } from "@/context/AuthContext"; // 🟢 Importă hook-ul de autentificare
import { EXPERIENCE_CATEGORIES } from "../enums";

interface ExperienceCardProps {
  experience: ExperienceDto;
}

// 🟢 Funcție utilitară pentru a detecta valori nule, nedefinite sau placeholder-e de tip "N/A"
const isInvalidValue = (value: any): boolean => {
  if (value === null || value === undefined) return true;
  const str = String(value).trim().toLowerCase();
  return str === "" || str === "null" || str === "undefined" || str === "n/a" || str === "na";
};

const ExperienceCard = ({ experience }: ExperienceCardProps) => {
  const { navigateTo } = useNavigation();
  const { myExperiences } = useAuth(); // 🟢 Extrage experiențele utilizatorului curent
  const [currentIndex, setCurrentIndex] = useState(0);

  // 🟢 Verifică dacă această experiență aparține utilizatorului logat
  const isOwnListing = useMemo(() => {
    if (!myExperiences || !experience.experienceId) return false;
    return myExperiences.some((myExp) => myExp.experienceId === experience.experienceId);
  }, [myExperiences, experience.experienceId]);

  // Parse Media & Image Fallbacks safely
  const images = useMemo(() => {
    if (experience.mediaUrls && experience.mediaUrls.length > 0) {
      return experience.mediaUrls.filter(url => url && url.trim() !== "");
    }

    if (experience.media && experience.media.length > 0) {
      return experience.media.map((m) => {
        const data = (m as any).mediaData;
        if (!data) return "";
        return data.startsWith("data:") ? data : `data:image/jpeg;base64,${data}`;
      }).filter(img => img !== "");
    }

    return [`https://via.placeholder.com/400x300?text=${encodeURIComponent(experience.title || "Flight Experience")}`];
  }, [experience.mediaUrls, experience.media, experience.title]);

  const formatPrice = (price?: number) => {
    if (!price || isInvalidValue(price) || price === 0) return "LA CERERE";
    return new Intl.NumberFormat("ro-RO", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0
    }).format(price);
  };

  const handleCardClick = () => {
    navigateTo("experience-details", {
      experienceId: experience.experienceId,
      experience: experience,
      seller: isOwnListing ? 1 : 0
    });
  };

  const nextImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const prevImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  // Rezolvare label-uri și curățare valori
  const cleanTitle = isInvalidValue(experience.title) ? "Experiență Aviatică" : experience.title;
  const cleanLocation = isInvalidValue(experience.location) ? null : experience.location;
  const cleanDescription = isInvalidValue(experience.description) ? null : experience.description;

  const rawCategory = EXPERIENCE_CATEGORIES.find(c => c.code === experience.type)?.label.toUpperCase() || experience.type;
  const categoryLabel = isInvalidValue(rawCategory) ? "ZBOR" : rawCategory;

  return (
    <div
      onClick={handleCardClick}
      className={`group relative bg-[#0F172A]/80 backdrop-blur-xl border rounded-3xl overflow-hidden cursor-pointer hover:border-red-500/50 transition-all duration-300 shadow-2xl h-full flex flex-col ${
        isOwnListing ? "border-dashed border-slate-700 opacity-95" : "border-slate-800"
      }`}
    >
      {/* IMAGE SECTION */}
      <div className="relative h-52 overflow-hidden shrink-0">
        <img
          src={images[currentIndex]}
          alt={cleanTitle}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
        />

        <div className="absolute top-3 left-3 flex flex-wrap gap-2 z-10 max-w-[calc(100%-24px)]">
          {/* 🟢 Adăugat: Badge premium pentru listările proprii, stilizat pe roșu-portocaliu */}
          {isOwnListing && (
            <span className="bg-slate-900/90 backdrop-blur-md border border-red-500/30 text-orange-400 text-[9px] font-black px-2 py-1 rounded-md uppercase tracking-widest shadow-lg animate-pulse">
              Propria listare
            </span>
          )}
          <span className="bg-red-600 text-white text-[9px] font-black px-2 py-1 rounded-md uppercase tracking-widest shadow-lg">
            {categoryLabel}
          </span>
          {experience.commissionModel && !isInvalidValue(experience.commissionModel) && (
            <span className="bg-slate-900/90 backdrop-blur-md border border-slate-700 text-white text-[9px] font-black px-2 py-1 rounded-md uppercase tracking-widest shadow-lg">
              B2B Partner
            </span>
          )}
        </div>

        {images.length > 1 && (
          <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 flex justify-between px-2 opacity-0 group-hover:opacity-100 transition-opacity z-20">
            <button onClick={prevImage} className="p-1.5 bg-slate-900/80 rounded-full text-white hover:bg-red-600 transition-colors">
              <ChevronLeft size={16} />
            </button>
            <button onClick={nextImage} className="p-1.5 bg-slate-900/80 rounded-full text-white hover:bg-red-600 transition-colors">
              <ChevronRight size={16} />
            </button>
          </div>
        )}
      </div>

      {/* CONTENT SECTION */}
      <div className="p-5 flex flex-col flex-1">
        <div className="flex-1 space-y-4">
          {/* Title & Classification */}
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Compass size={14} className="text-red-500 animate-pulse" />
              <h3 className="text-lg font-black text-white tracking-tight uppercase truncate">
                {cleanTitle}
              </h3>
            </div>

            {/* 🟢 MODIFICAT: Se ascunde elementul dacă locația este nulă */}
            {cleanLocation && (
              <div className="flex items-center gap-1 text-slate-500">
                <MapPin size={12} />
                <span className="text-[11px] font-bold uppercase tracking-wider truncate">
                  {cleanLocation}
                </span>
              </div>
            )}
          </div>

          {/* CONTAINER CU ÎNALȚIME FIXĂ PENTRU DESCRIERE */}
          <div className="h-24 overflow-hidden">
            {/* 🟢 MODIFICAT: Se folosește verificarea de nulitate pe descriere */}
            {cleanDescription ? (
              <p className="text-[12px] text-slate-400 leading-relaxed line-clamp-4">
                {cleanDescription}
              </p>
            ) : (
              <p className="text-[12px] text-slate-600 italic">
                Nicio descriere disponibilă pentru această experiență.
              </p>
            )}
          </div>
        </div>

        {/* PRICING FOOTER */}
        <div className="flex items-end justify-between mt-5 pt-4 border-t border-slate-800/50">
          <div>
            <p className="text-[9px] font-bold text-slate-500 uppercase tracking-[0.2em]">Tarif Client</p>
            <div className="flex items-baseline gap-1">
              <span className="text-2xl font-black text-white tracking-tighter">
                {formatPrice(experience.grossValue)}
              </span>
            </div>
          </div>
          <div className="text-right">
             <button className="bg-red-500/10 hover:bg-red-500 text-red-400 hover:text-white p-2 rounded-xl border border-red-500/20 transition-all">
                <ChevronRight size={18} />
             </button>
          </div>
        </div>
      </div>

      {/* DECORATIVE LINE */}
      <div className="absolute bottom-0 left-0 h-1 bg-red-500 transition-all duration-300 w-0 group-hover:w-full" />
    </div>
  );
};

export default ExperienceCard;